import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FreightResponse, SearchResponse, ConfirmationReview, ConfirmationUploadResult } from './freight.models';

@Injectable({ providedIn: 'root' })
export class FreightService {
  private readonly baseUrl = '/api/work_orders/freight/get_freight.php';

  constructor(private http: HttpClient) {}

  /** Load full freight context for a given lading UUID */
  load(ladingUuid: string): Observable<FreightResponse> {
    const params = new HttpParams().set('uuid', ladingUuid);
    return this.http.get<FreightResponse>(this.baseUrl, { params });
  }

  reviewConfirmation(ladingUuid: string): Observable<ConfirmationReview> {
    const params = new HttpParams().set('lading_uuid', ladingUuid);
    return this.http.get<ConfirmationReview>('/api/work_orders/freight/confirmation_review.php', { params });
  }

  approveConfirmation(review: ConfirmationReview, reason: string): Observable<ConfirmationUploadResult> {
    return this.http.post<ConfirmationUploadResult>('/api/work_orders/freight/confirmation_review.php', {
      action: 'approve_upload',
      lading_uuid: review.lading_uuid,
      review_token: review.review_token,
      approved: true,
      reason: reason.trim()
    });
  }

  /** Search by item code */
  searchByCode(code: string): Observable<SearchResponse> {
    const params = new HttpParams().set('search_code', code);
    return this.http.get<SearchResponse>(this.baseUrl, { params });
  }

  /** Search by serial number */
  searchBySerial(serial: string): Observable<SearchResponse> {
    const params = new HttpParams().set('search_serial', serial);
    return this.http.get<SearchResponse>(this.baseUrl, { params });
  }


   updateProduct(
    productUuid: string,
    changes: { [key: string]: any }
  ): Observable<any> {
    const payload = {
      action: 'update_product',
      product_uuid: productUuid,
      changes
    };
    return this.http.put(this.baseUrl, payload);
  }

  /**
   * Generic search: either code or serial must be provided.
   * @throws if neither code nor serial is passed
   */
  search(code?: string, serial?: string): Observable<SearchResponse> {
    let params = new HttpParams();
    if (code) {
      params = params.set('search_code', code);
    } else if (serial) {
      params = params.set('search_serial', serial);
    } else {
      throw new Error('FreightService.search: either code or serial must be provided');
    }
    return this.http.get<SearchResponse>(this.baseUrl, { params });
  }

  /**
   * Attach (add) a package to the lading.
   * Returns whatever the backend proxy emits (typically a JSON success payload).
   */
  attach(
    ladingUuid: string,
    packageUuid: string,
    productUuid: string
  ): Observable<unknown> {
    const body = {
      action:        'attach',
      lading_uuid:   ladingUuid,
      package_uuid:  packageUuid,
      product_uuid:  productUuid
    };
    return this.http.post<unknown>(this.baseUrl, body);
  }

  /**
   * Detach (remove) a package from the lading.
   * Returns whatever the backend proxy emits (typically a JSON success payload).
   */
  detach(ladingPackageUuid: string): Observable<unknown> {
    const params = new HttpParams().set('lading_package_uuid', ladingPackageUuid);
    return this.http.delete<unknown>(this.baseUrl, { params });
  }
}
